import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, ChevronRight, ClipboardCheck, Camera, Save,
  Send, CheckCircle2, AlertTriangle, Trash2, Upload, WifiOff, Lock,
} from "lucide-react";
import { SignaturePad } from "../../components/team/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [
  { key: "pre", label: "Pre-inspection" },
  { key: "fitment", label: "Fitment" },
  { key: "post", label: "Post-inspection" },
  { key: "signoff", label: "Sign-off" },
];

const PRE_DISCLAIMER =
  "I acknowledge that Diesel & Dust Windscreens has walked through the vehicle with me and " +
  "that the pre-fitment condition of my vehicle has been captured photographically. Any " +
  "pre-existing damage noted above is not the responsibility of Diesel & Dust Windscreens.";
const POST_DISCLAIMER =
  "Your windscreen requires time to bond to the body of the car. Driving your car too soon " +
  "may result in wind noise, water leaks or impaired safety. We recommend that you allow " +
  "your vehicle to stand for 120 minutes (240 minutes with airbags) after the windscreen " +
  "replacement. Please remove the safety tape after one day. Ensure that all valuables are " +
  "removed from the vehicle before the fitment, as we will not be liable for any losses or theft.";

export default function JobCardStepper() {
  const { id: jobId } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true); const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/team/my/jobs/${jobId}/jobcard`);
      setCard(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load job card"); nav("/team", { replace: true }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const locked = card?.status === "submitted" || card?.status === "approved";
  const patch = (obj) => setCard((c) => ({ ...c, ...obj }));

  // Debounced autosave
  const timer = useRef(null);
  const dirty = useRef(false);
  useEffect(() => {
    if (!card || locked || !online) return;
    if (!dirty.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const body = {
          vehicle_details: card.vehicle_details, etag: card.etag, licence_disc: card.licence_disc,
          pre_damage_markers: card.pre_damage_markers, post_damage_markers: card.post_damage_markers,
          photos: card.photos, condition: card.condition, operation: card.operation,
          components: card.components, adas: card.adas, remarks: card.remarks,
          overall_rating: card.overall_rating, keys_handed_over: card.keys_handed_over,
          pre_signature: card.pre_signature, post_customer_signature: card.post_customer_signature,
          fitter_signature: card.fitter_signature, keys_handover_signature: card.keys_handover_signature,
        };
        await axios.put(`${API}/team/my/jobs/${jobId}/jobcard`, body);
        dirty.current = false;
      } catch (err) { /* silent */ }
    }, 800);
    return () => clearTimeout(timer.current);
  }, [card, jobId, locked, online]);

  const dirtyPatch = (obj) => { dirty.current = true; patch(obj); };

  const saveNow = async () => {
    if (locked || !online) return;
    setBusy(true);
    try {
      await axios.put(`${API}/team/my/jobs/${jobId}/jobcard`, card);
      toast.success("Draft saved");
      dirty.current = false;
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!online) return toast.error("Offline — submit requires connectivity");
    if (!window.confirm("Submit this job card to the admin office? You will not be able to edit until it is returned or approved.")) return;
    setBusy(true); setSubmitErrors([]);
    try {
      await axios.put(`${API}/team/my/jobs/${jobId}/jobcard`, card);
      const r = await axios.post(`${API}/team/my/jobs/${jobId}/jobcard/submit`);
      toast.success("Submitted for review");
      setCard((c) => ({ ...c, ...r.data }));
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && d.validation_errors) { setSubmitErrors(d.validation_errors); toast.error(`${d.validation_errors.length} validation issue(s)`); }
      else toast.error(d || "Submit failed");
    } finally { setBusy(false); }
  };

  if (!card) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#f28a16]" /></div>;

  const ts = card.template_snapshot;

  return (
    <div className="space-y-4" data-testid="jobcard-stepper">
      <Link to={`/team/jobs/${jobId}`} className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">← Back to job</Link>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display font-black uppercase text-2xl text-[#111619]">Job Card · #{card.job_number}</h1>
        <span data-testid="jc-status"
              className={`text-xs px-2 py-1 rounded-sm font-display uppercase tracking-widest ${locked ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
          {card.status}{locked && <Lock size={10} className="inline ml-1" />}
        </span>
      </div>

      {card.status === "returned" && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs" data-testid="jc-returned-banner">
          <AlertTriangle size={14} className="inline mr-1 text-amber-700" />
          Returned by admin. Latest note:{" "}
          <b>{card.return_history?.slice(-1)?.[0]?.note || "—"}</b>. Any edit will move this back to Draft.
        </div>
      )}
      {!online && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs" data-testid="jc-offline">
          <WifiOff size={14} className="inline mr-1" /> Offline — job-card writes are disabled until you reconnect.
        </div>
      )}

      {/* Stepper */}
      <div className="grid grid-cols-4 gap-1 bg-white border border-[#cfd7db] rounded-md p-1" data-testid="jc-tabs">
        {STEPS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)} data-testid={`jc-step-${s.key}`}
                  className={`py-2 text-xs font-display uppercase tracking-widest rounded transition ${i === step ? "bg-[#f28a16] text-white" : "text-[#3f4b52]"}`}>
            {i + 1}. {s.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#cfd7db] rounded-md p-4 space-y-4">
        {STEPS[step].key === "pre" && (
          <PreStep card={card} patch={dirtyPatch} locked={locked} jobId={jobId} />
        )}
        {STEPS[step].key === "fitment" && (
          <FitmentStep card={card} patch={dirtyPatch} locked={locked} ts={ts} jobId={jobId} />
        )}
        {STEPS[step].key === "post" && (
          <PostStep card={card} patch={dirtyPatch} locked={locked} jobId={jobId} />
        )}
        {STEPS[step].key === "signoff" && (
          <SignoffStep card={card} patch={dirtyPatch} locked={locked} />
        )}
      </div>

      {submitErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs" data-testid="jc-errors">
          <div className="font-display uppercase tracking-widest text-red-700 mb-1">Fix these before submitting</div>
          <ul className="list-disc list-inside space-y-0.5">{submitErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                data-testid="jc-prev"
                className="inline-flex items-center gap-1 px-3 py-2 border rounded-md text-xs font-display uppercase tracking-widest disabled:opacity-40">
          <ChevronLeft size={14} /> Previous
        </button>
        <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={step === STEPS.length - 1}
                data-testid="jc-next"
                className="inline-flex items-center gap-1 px-3 py-2 border rounded-md text-xs font-display uppercase tracking-widest disabled:opacity-40">
          Next <ChevronRight size={14} />
        </button>
        <div className="flex-1" />
        <button onClick={saveNow} disabled={locked || busy || !online} data-testid="jc-save"
                className="inline-flex items-center gap-1 px-3 py-2 border border-[#f28a16] text-[#f28a16] rounded-md text-xs font-display uppercase tracking-widest disabled:opacity-40">
          <Save size={14} /> Save draft
        </button>
        <button onClick={submit} disabled={locked || busy || !online} data-testid="jc-submit"
                className="inline-flex items-center gap-1 px-3 py-2 bg-[#f28a16] text-white rounded-md text-xs font-display uppercase tracking-widest disabled:opacity-40">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Submit to admin
        </button>
      </div>
    </div>
  );
}

/* ==================== Sections ==================== */

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">{label}</span>
    {children}
  </label>
);

const YesNo = ({ value, onChange, testid, options = ["Yes", "No"] }) => (
  <div className="flex gap-1" data-testid={testid}>
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o === "Yes")}
              data-testid={`${testid}-${o.toLowerCase()}`}
              className={`px-3 py-1.5 rounded text-xs font-display uppercase tracking-widest ${
                value === (o === "Yes") ? "bg-[#f28a16] text-white" : "bg-white border border-[#cfd7db] text-[#3f4b52]"
              }`}>
        {o}
      </button>
    ))}
  </div>
);

const PhotoSlot = ({ label, docIds, onChange, purpose, jobId, locked, testid, help, min = 0 }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const upload = async (files) => {
    if (locked) return;
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10MB`); continue; }
        const fd = new FormData();
        fd.append("purpose", purpose || "damage_photo");
        fd.append("job_id", jobId);
        fd.append("file", f);
        const r = await axios.post(`${API}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.id);
      }
      onChange([...(docIds || []), ...uploaded]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="border border-[#cfd7db] rounded-md p-2.5" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-display uppercase tracking-widest text-[#111619]">{label}</div>
          {help && <div className="text-xs text-[#3f4b52]">{help}</div>}
        </div>
        <span className="text-xs text-[#3f4b52]">{(docIds || []).length}{min ? `/${min}` : ""}</span>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)}
             data-testid={`${testid}-input`} disabled={locked || busy} />
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(docIds || []).map((did) => <PhotoThumb key={did} id={did} onRemove={() => onChange(docIds.filter((x) => x !== did))} locked={locked} />)}
        {!locked && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                  className="w-14 h-14 border-2 border-dashed border-[#f28a16] rounded-md flex items-center justify-center text-[#f28a16] disabled:opacity-40"
                  data-testid={`${testid}-add`}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Camera size={16} />}
          </button>
        )}
      </div>
    </div>
  );
};

const PhotoThumb = ({ id, onRemove, locked }) => {
  const [tok, setTok] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/documents/${id}/sign`); setTok(r.data.download_token); }
      catch { /* silent */ }
    })();
  }, [id]);
  return (
    <div className="relative">
      <img src={tok ? `${API}/documents/${id}/download?tok=${tok}` : ""} alt="" className="w-14 h-14 rounded-md object-cover border" />
      {!locked && (
        <button type="button" onClick={onRemove} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 text-xs">
          <Trash2 size={10} className="mx-auto" />
        </button>
      )}
    </div>
  );
};

const PreStep = ({ card, patch, locked, jobId }) => {
  const ts = card.template_snapshot;
  const preSlots = ts.photo_slots.filter((s) => s.phase === "pre");
  const vd = card.vehicle_details || {};
  const setVd = (k, v) => patch({ vehicle_details: { ...vd, [k]: v } });
  const setEtag = (k, v) => patch({ etag: { ...(card.etag || {}), [k]: v } });
  const setLD = (k, v) => patch({ licence_disc: { ...(card.licence_disc || {}), [k]: v } });
  const setPhotos = (key, ids) => patch({ photos: { ...(card.photos || {}), [key]: ids } });

  return (
    <div className="space-y-4">
      <div>
        <div className="font-display uppercase tracking-widest text-sm">Vehicle details</div>
        <p className="text-xs text-[#3f4b52]">Auto-filled from the job — confirm and complete the missing fields.</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="VIN number *"><input disabled={locked} value={vd.vin_number || ""} onChange={(e) => setVd("vin_number", e.target.value)}
                                            data-testid="jc-vin" className="w-full h-10 border rounded px-2 text-sm" /></Field>
        <Field label="Engine no. *"><input disabled={locked} value={vd.engine_number || ""} onChange={(e) => setVd("engine_number", e.target.value)}
                                            data-testid="jc-engine" className="w-full h-10 border rounded px-2 text-sm" /></Field>
        <Field label="KM reading *"><input disabled={locked} type="number" value={vd.km_reading ?? ""} onChange={(e) => setVd("km_reading", e.target.value ? Number(e.target.value) : null)}
                                            data-testid="jc-km" className="w-full h-10 border rounded px-2 text-sm" /></Field>
      </div>

      <div className="border-t border-[#cfd7db] pt-3">
        <div className="font-display uppercase tracking-widest text-sm">E-tag &amp; Licence disc</div>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mb-1">E-tag present?</div>
            <YesNo value={card.etag?.present} onChange={(v) => setEtag("present", v)} testid="jc-etag-yesno" />
            {card.etag?.present && (
              <>
                <input disabled={locked} value={card.etag?.id || ""} onChange={(e) => setEtag("id", e.target.value)}
                       placeholder="Optional identifier"
                       data-testid="jc-etag-id" className="w-full h-9 border rounded px-2 text-sm mt-2" />
                <div className="mt-2">
                  <PhotoSlot testid="jc-etag-photo" label="E-tag photo *" purpose="damage_photo" jobId={jobId} locked={locked}
                             docIds={card.etag?.photo_doc_id ? [card.etag.photo_doc_id] : []}
                             onChange={(ids) => setEtag("photo_doc_id", ids[0] || null)} min={1} />
                </div>
              </>
            )}
          </div>
          <div>
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mb-1">Licence disc present?</div>
            <YesNo value={card.licence_disc?.present} onChange={(v) => setLD("present", v)} testid="jc-disc-yesno" />
            {card.licence_disc?.present && (
              <div className="mt-2">
                <PhotoSlot testid="jc-disc-photo" label="Licence disc photo *" purpose="damage_photo" jobId={jobId} locked={locked}
                           docIds={card.licence_disc?.photo_doc_id ? [card.licence_disc.photo_doc_id] : []}
                           onChange={(ids) => setLD("photo_doc_id", ids[0] || null)} min={1} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-[#cfd7db] pt-3">
        <div className="font-display uppercase tracking-widest text-sm">Pre-inspection photos</div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {preSlots.map((s) => (
            <PhotoSlot key={s.key} testid={`jc-photo-${s.key}`} label={s.required ? `${s.label} *` : s.label}
                       help={s.help} purpose="damage_photo" jobId={jobId} locked={locked} min={s.min_count}
                       docIds={card.photos?.[s.key] || []} onChange={(ids) => setPhotos(s.key, ids)} />
          ))}
        </div>
      </div>

      <div className="border-t border-[#cfd7db] pt-3">
        <div className="font-display uppercase tracking-widest text-sm">Pre-inspection damage markers</div>
        <p className="text-[11px] text-[#3f4b52]">B = Broken · C = Chip · D = Dent · S = Scratch. Tap on the diagram to add.</p>
        <DamageMarkersEditor markers={card.pre_damage_markers || []}
                             onChange={(m) => patch({ pre_damage_markers: m })} locked={locked} testid="jc-pre-diagram" />
      </div>

      <div className="border-t border-[#cfd7db] pt-3">
        <div className="text-xs text-[#3f4b52] italic bg-[#f5f6f6] p-3 rounded-md" data-testid="jc-pre-disclaimer">
          {PRE_DISCLAIMER}
        </div>
        <div className="mt-2">
          <SigCapture value={card.pre_signature} onChange={(sig) => patch({ pre_signature: sig })}
                      locked={locked} role="customer" label="Pre-inspection acknowledgement signature" testid="jc-pre-sig" />
        </div>
      </div>
    </div>
  );
};

const FitmentStep = ({ card, patch, locked, ts, jobId }) => {
  const setCond = (row, key, v) => patch({ condition: { ...(card.condition || {}), [row]: { ...(card.condition?.[row] || {}), [key]: v } } });
  const setOp = (row, key, v) => patch({ operation: { ...(card.operation || {}), [row]: { ...(card.operation?.[row] || {}), [key]: v } } });
  const setComp = (row, key, v) => patch({ components: { ...(card.components || {}), [row]: { ...(card.components?.[row] || {}), [key]: v } } });

  return (
    <div className="space-y-5">
      <div>
        <div className="font-display uppercase tracking-widest text-sm">Replaced &amp; Condition</div>
        <p className="text-xs text-[#3f4b52]">Exceptions (Damaged / Stained / Perished) require a note and a photo.</p>
      </div>
      <MatrixTable rows={ts.condition_rows} states={ts.condition_states}
                   values={card.condition || {}}
                   onSet={setCond} needNoteOn={["Damaged", "Stained", "Perished"]}
                   jobId={jobId} locked={locked} testidPrefix="jc-cond" />

      <div className="border-t pt-3">
        <div className="font-display uppercase tracking-widest text-sm">Operation Checked / Working</div>
        <p className="text-xs text-[#3f4b52]">Any &apos;No&apos; requires a note + evidence photo.</p>
      </div>
      <MatrixTable rows={ts.operation_rows} states={ts.operation_states}
                   values={card.operation || {}}
                   onSet={setOp} needNoteOn={["No"]}
                   jobId={jobId} locked={locked} testidPrefix="jc-op" />

      <div className="border-t pt-3">
        <div className="font-display uppercase tracking-widest text-sm">Fitment Components</div>
      </div>
      <MatrixTable rows={ts.component_rows} states={ts.component_states}
                   values={card.components || {}}
                   onSet={setComp} needNoteOn={[]}
                   jobId={jobId} locked={locked} testidPrefix="jc-comp" />

      <div className="border-t pt-3 space-y-2">
        <div className="font-display uppercase tracking-widest text-sm">ADAS Calibration</div>
        <Field label="State">
          <select disabled={locked} value={card.adas?.state || ""}
                  onChange={(e) => patch({ adas: { ...(card.adas || {}), state: e.target.value } })}
                  data-testid="jc-adas-state" className="w-full h-10 border rounded px-2 text-sm">
            <option value="">Choose…</option>
            {ts.adas_states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Reference / calibration ID (optional)">
          <input disabled={locked} value={card.adas?.reference || ""} onChange={(e) => patch({ adas: { ...(card.adas || {}), reference: e.target.value } })}
                 data-testid="jc-adas-ref" className="w-full h-10 border rounded px-2 text-sm" />
        </Field>
      </div>
    </div>
  );
};

const MatrixTable = ({ rows, states, values, onSet, needNoteOn, jobId, locked, testidPrefix }) => (
  <div className="space-y-1.5">
    {rows.map((row) => {
      const v = values[row] || {};
      const needsNote = needNoteOn.includes(v.state);
      return (
        <div key={row} className="border border-[#cfd7db] rounded-md p-2" data-testid={`${testidPrefix}-row-${row.replace(/\W+/g, "-")}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-[#111619] min-w-[45%]">{row}</span>
            <div className="flex flex-wrap gap-1">
              {states.map((s) => (
                <button key={s} type="button" disabled={locked} onClick={() => onSet(row, "state", s)}
                        data-testid={`${testidPrefix}-${row.replace(/\W+/g, "-")}-${s.replace(/\W+/g, "-")}`}
                        className={`text-xs font-display uppercase tracking-widest px-2 py-1 rounded ${v.state === s ? "bg-[#111619] text-white" : "bg-white border border-[#cfd7db] text-[#3f4b52]"}`}>{s}</button>
              ))}
            </div>
          </div>
          {needsNote && (
            <div className="mt-2 grid gap-2">
              <input disabled={locked} value={v.note || ""} onChange={(e) => onSet(row, "note", e.target.value)}
                     placeholder="Note *" className="w-full h-9 border rounded px-2 text-sm" />
              <PhotoSlot testid={`${testidPrefix}-photo-${row.replace(/\W+/g, "-")}`} label="Evidence photo *"
                         purpose="damage_photo" jobId={jobId} locked={locked} min={1}
                         docIds={v.photo_doc_id ? [v.photo_doc_id] : []}
                         onChange={(ids) => onSet(row, "photo_doc_id", ids[0] || null)} />
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const PostStep = ({ card, patch, locked, jobId }) => {
  const ts = card.template_snapshot;
  const postSlots = ts.photo_slots.filter((s) => s.phase === "post");
  const setPhotos = (key, ids) => patch({ photos: { ...(card.photos || {}), [key]: ids } });
  return (
    <div className="space-y-4">
      <div>
        <div className="font-display uppercase tracking-widest text-sm">Post-inspection photos</div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {postSlots.map((s) => (
          <PhotoSlot key={s.key} testid={`jc-photo-${s.key}`} label={s.required ? `${s.label} *` : s.label} help={s.help}
                     purpose="damage_photo" jobId={jobId} locked={locked} min={s.min_count}
                     docIds={card.photos?.[s.key] || []} onChange={(ids) => setPhotos(s.key, ids)} />
        ))}
      </div>
      <div className="border-t pt-3">
        <div className="font-display uppercase tracking-widest text-sm">Post-fitment damage markers</div>
        <DamageMarkersEditor markers={card.post_damage_markers || []}
                             onChange={(m) => patch({ post_damage_markers: m })} locked={locked} testid="jc-post-diagram" />
      </div>
      <div className="border-t pt-3">
        <div className="text-xs text-[#3f4b52] italic bg-[#f5f6f6] p-3 rounded-md" data-testid="jc-post-disclaimer">
          {POST_DISCLAIMER}
        </div>
      </div>
    </div>
  );
};

const SignoffStep = ({ card, patch, locked }) => {
  return (
    <div className="space-y-4">
      <div className="font-display uppercase tracking-widest text-sm">Overall &amp; sign-off</div>
      <div className="grid grid-cols-3 gap-1" data-testid="jc-rating">
        {["Good", "Acceptable", "Bad"].map((r) => (
          <button key={r} disabled={locked} onClick={() => patch({ overall_rating: r })}
                  data-testid={`jc-rating-${r}`}
                  className={`py-2 rounded font-display uppercase tracking-widest text-xs ${card.overall_rating === r ? "bg-[#f28a16] text-white" : "bg-white border border-[#cfd7db] text-[#3f4b52]"}`}>{r}</button>
        ))}
      </div>
      <Field label="Remarks *">
        <textarea disabled={locked} rows={3} value={card.remarks || ""} onChange={(e) => patch({ remarks: e.target.value })}
                  data-testid="jc-remarks" className="w-full border rounded p-2 text-sm" />
      </Field>

      <SigCapture value={card.post_customer_signature} onChange={(sig) => patch({ post_customer_signature: sig })}
                  locked={locked} role="customer" label="Customer signature — post-fitment" testid="jc-post-sig" />

      <SigCapture value={card.fitter_signature} onChange={(sig) => patch({ fitter_signature: sig })}
                  locked={locked} role="fitter" label="Fitter signature" testid="jc-fitter-sig" />

      <div className="border-t pt-3">
        <div className="font-display uppercase tracking-widest text-sm mb-2">Keys handed over</div>
        <YesNo value={card.keys_handed_over} onChange={(v) => patch({ keys_handed_over: v })} testid="jc-keys-yesno" />
        {card.keys_handed_over && (
          <div className="mt-2">
            <SigCapture value={card.keys_handover_signature} onChange={(sig) => patch({ keys_handover_signature: sig })}
                        locked={locked} role="customer" label="Keys handover signature" testid="jc-keys-sig" />
          </div>
        )}
      </div>
    </div>
  );
};

const SigCapture = ({ value, onChange, locked, role, label, testid }) => {
  const [signerName, setSignerName] = useState(value?.signer_name || "");
  const onDone = (dataUrl) => {
    onChange({ role, signer_name: signerName.trim(), data_url: dataUrl, at: new Date().toISOString() });
  };
  return (
    <div className="border border-[#cfd7db] rounded-md p-3 space-y-2" data-testid={testid}>
      <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">{label}</div>
      <input disabled={locked} value={signerName} onChange={(e) => setSignerName(e.target.value)}
             placeholder="Signer name" data-testid={`${testid}-name`}
             className="w-full h-9 border rounded px-2 text-sm" />
      {value?.data_url ? (
        <div className="flex items-center gap-2">
          <img src={value.data_url} alt="signature" className="border rounded max-h-24" />
          {!locked && (
            <button type="button" onClick={() => onChange(null)} className="text-xs text-red-600"
                    data-testid={`${testid}-clear`}>Clear</button>
          )}
        </div>
      ) : locked ? (
        <div className="text-xs text-[#3f4b52]">— No signature —</div>
      ) : (
        <SignaturePadWrapper onDone={onDone} disabled={!signerName.trim()} testid={testid} />
      )}
    </div>
  );
};

const SignaturePadWrapper = ({ onDone, disabled, testid }) => {
  const [val, setVal] = useState(null);
  const handle = (dataUrl) => { setVal(dataUrl); if (dataUrl) onDone(dataUrl); };
  return (
    <div>
      {disabled && <div className="text-[11px] text-red-600 mb-1">Enter the signer name first</div>}
      <SignaturePad label="Sign here" value={val} onChange={handle} testid={`${testid}-pad`} />
    </div>
  );
};

/* ---- Damage markers editor (extends existing DamageDiagram) ---- */
const VIEWS = ["top", "front", "rear", "left", "right"];
const KINDS = [
  { k: "B", label: "Broken", colour: "#ef4444" },
  { k: "C", label: "Chip", colour: "#f59e0b" },
  { k: "D", label: "Dent", colour: "#0ea5e9" },
  { k: "S", label: "Scratch", colour: "#8b5cf6" },
];

const DamageMarkersEditor = ({ markers, onChange, locked, testid }) => {
  const [view, setView] = useState("top");
  const [kind, setKind] = useState("C");
  const svgRef = useRef();
  const shown = markers.filter((m) => m.view === view);

  const click = (e) => {
    if (locked) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    onChange([...markers, { view, x: +x.toFixed(3), y: +y.toFixed(3), kind, note: "" }]);
  };

  return (
    <div data-testid={testid} className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {VIEWS.map((v) => (
          <button key={v} type="button" onClick={() => setView(v)}
                  data-testid={`${testid}-view-${v}`}
                  className={`text-xs font-display uppercase tracking-widest px-2 py-1 rounded ${view === v ? "bg-[#111619] text-white" : "bg-white border border-[#cfd7db]"}`}>{v}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {KINDS.map((k) => (
          <button key={k.k} type="button" onClick={() => setKind(k.k)}
                  data-testid={`${testid}-kind-${k.k}`}
                  className={`text-xs font-display uppercase tracking-widest px-2 py-1 rounded ${kind === k.k ? "bg-[#f28a16] text-white" : "bg-white border border-[#cfd7db]"}`}>
            {k.k}={k.label}
          </button>
        ))}
      </div>
      <svg ref={svgRef} viewBox="0 0 400 200" onClick={click}
           className="w-full max-w-md bg-[#f5f6f6] border border-[#cfd7db] rounded cursor-crosshair select-none">
        <rect x="40" y="40" width="320" height="120" rx="30" fill="#edf2f4" stroke="#9aa6ad" />
        <text x="200" y="26" textAnchor="middle" className="text-xs" fill="#3f4b52">{view.toUpperCase()} view</text>
        {shown.map((m, i) => {
          const cx = m.x * 400, cy = m.y * 200;
          const meta = KINDS.find((k) => k.k === m.kind) || KINDS[0];
          return (
            <g key={i}>
              <circle cx={cx} cy={cy} r="9" fill={meta.colour} opacity="0.85" />
              <text x={cx} y={cy + 4} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">{m.kind}</text>
            </g>
          );
        })}
      </svg>
      {shown.length > 0 && (
        <ul className="space-y-1 text-xs">
          {shown.map((m, i) => {
            const globalIdx = markers.findIndex((x) => x === m);
            return (
              <li key={i} className="flex items-center gap-2">
                <span className="font-mono">{m.kind}</span>
                <input disabled={locked} value={m.note || ""} placeholder="note"
                       onChange={(e) => { const c = [...markers]; c[globalIdx] = { ...m, note: e.target.value }; onChange(c); }}
                       className="flex-1 h-8 border rounded px-2" />
                {!locked && (
                  <button type="button" onClick={() => onChange(markers.filter((_, j) => j !== globalIdx))} className="text-red-600 text-xs">
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
